import { FormControlLabel, Radio, RadioGroup, Snackbar, Stack, TextField } from '@mui/material'
import { useState } from 'react'
import { Alert } from 'ComponentLibrary/src/molecules/Alert'
import { InfoTooltip, tokens } from 'ComponentLibrary'
import { Checkbox } from 'ComponentLibrary/src/piazza/selectors'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { PaperPlaneTiltIcon } from '@phosphor-icons/react'
import { TextButtonNew } from 'ComponentLibrary/src/piazza/buttons/TextButton/TextButtonNew'
import { IconButton } from 'ComponentLibrary/src/piazza/buttons/IconButton/IconButton'
import { TinyButton } from 'ComponentLibrary/src/piazza/buttons'
import { Modal } from 'ComponentLibrary/src/piazza/modals'

type FeedbackToolOptionCopy = {
    title: string
    description: string
}

export type FeedbackToolCopy = {
    triggerLabel: string
    tooltip: string
    title: string
    close: string
    cancel: string
    submit: string
    categoryLabel: string
    descriptionLabel: string
    descriptionTooltip: string
    descriptionFieldLabel: string
    descriptionAriaLabel: string
    incidentLabel: string
    incidentTooltip: string
    errorMessage: string
    successMessage: string
    options: FeedbackToolOptionCopy[]
}

const DEFAULT_FEEDBACK_TOOL_COPY: FeedbackToolCopy = {
    triggerLabel: 'Feedback geben',
    tooltip: 'Feedback geben',
    title: 'Feedback geben',
    close: 'Schließen',
    cancel: 'Abbrechen',
    submit: 'Absenden',
    categoryLabel: 'Art des Feedbacks',
    descriptionLabel: 'Beschreibung',
    descriptionTooltip: 'Konkrete Beispiele helfen uns am meisten.',
    descriptionFieldLabel: 'Was ist passiert? Was haben Sie erwartet?',
    descriptionAriaLabel: 'Beschreibung',
    incidentLabel: 'Kritisches Anliegen',
    incidentTooltip: 'Kritische Anliegen sind z.B. Fehler, die dich blockieren oder Situationen, in denen du umgehend Hilfe brauchst.',
    errorMessage:
        'Leider ist beim Absenden des Feedbacks ein Fehler aufgetreten. Bitte stellen Sie sicher, dass ihr Adblocker auf dieser Seite deaktiviert ist und versuchen Sie es nochmal. Sollte der Fehler weiterhin auftreten, melden Sie sich bitte bei support@einwert.com',
    successMessage: 'Vielen Dank für Ihr Feedback!',
    options: [
        {
            title: 'Etwas funktioniert nicht',
            description: 'z.B. Fehlermeldung, falsches Ergebnis, Absturz',
        },
        {
            title: 'Ergebnis war ungenau',
            description: 'z.B. falsche Interpretation durch KI, fehlende Antworten',
        },
        {
            title: 'Etwas ist unverständlich',
            description: 'z.B. Unklare Bedienung, verwirrender Output',
        },
        {
            title: 'Verbesserungsvorschlag',
            description: 'z.B. Idee, fehlende Funktion, Wunsch',
        },
        {
            title: 'Lob',
            description: 'z.B. Zeitersparnis, spürbare Hilfe',
        },
    ],
}

export type FeedbackToolProps = {
    onOpen: () => void
    onClose?: () => void
    sendFeedback: ({ feedback, category, isIncident }: { feedback: string; category: string; isIncident: boolean }) => Promise<void>
    isOpen?: boolean
    size?: 'small' | 'medium' | 'tiny'
    hideTrigger?: boolean
    showIncidentOption?: boolean
    copy?: FeedbackToolCopy
}

export function FeedbackTool({
    onOpen,
    onClose,
    sendFeedback,
    isOpen,
    size = 'medium',
    hideTrigger = false,
    showIncidentOption = true,
    copy = DEFAULT_FEEDBACK_TOOL_COPY,
}: FeedbackToolProps) {
    const [isUncontrolledOpen, setIsUncontrolledOpen] = useState<boolean>(false)
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false)
    const [isFeedbackError, setIsFeedbackError] = useState<boolean>(false)

    const [feedback, setFeedback] = useState<string>('')
    const [category, setCategory] = useState<string>('')
    const [isIncident, setIsIncident] = useState<boolean>(false)
    const [isSendingFeedback, setIsSendingFeedback] = useState<boolean>(false)

    const isModalOpen = isOpen ?? isUncontrolledOpen
    const hasSelectedCategory = category !== ''

    const handleModalOpen = () => {
        setIsUncontrolledOpen(true)
        onOpen()
    }

    const handleModalClose = () => {
        setIsUncontrolledOpen(false)
        onClose?.()
    }

    const handleSendFeedback = () => {
        if (!hasSelectedCategory) {
            return
        }

        setIsSendingFeedback(true)
        sendFeedback({ feedback: feedback.trim() === '' ? '-' : feedback, category, isIncident: showIncidentOption ? isIncident : false })
            .then(() => {
                setSnackbarOpen(true)
                handleModalClose()
                setFeedback('')
                setCategory('')
                setIsIncident(false)
                setIsFeedbackError(false)
                setIsSendingFeedback(false)
            })
            .catch(() => {
                setIsFeedbackError(true)
                setIsSendingFeedback(false)
            })
    }

    const handleCloseSnackbar = () => setSnackbarOpen(false)
    const handleCloseErrorSnackbar = () => setIsFeedbackError(false)

    const options = copy.options.map((option) => (
        <FormControlLabel
            key={option.title}
            value={option.title}
            control={<Radio size="small" color="success" />}
            label={
                <Stack direction="column" gap={tokens.SizingSpacing025}>
                    <Typography variant="textS" color={tokens.ColorTextBold}>
                        {option.title}
                    </Typography>
                    <Typography variant="textXS" color={tokens.ColorTextDefault}>
                        {option.description}
                    </Typography>
                </Stack>
            }
            sx={{
                alignItems: 'center',
                marginBottom: tokens.SizingSpacing150,
                '.MuiFormControlLabel-label': { display: 'flex' },
            }}
        />
    ))

    return (
        <>
            {hideTrigger ? null : size === 'tiny' ? (
                <TinyButton label={copy.triggerLabel} leftIcon={<PaperPlaneTiltIcon />} onClick={handleModalOpen} />
            ) : size === 'medium' ? (
                <TextButtonNew leftIcon={<PaperPlaneTiltIcon />} onClick={handleModalOpen} label={copy.triggerLabel} variant="secondary" />
            ) : (
                <IconButton
                    icon={<PaperPlaneTiltIcon />}
                    onClick={handleModalOpen}
                    size="medium"
                    variant="secondary"
                    tooltip={copy.tooltip}
                />
            )}
            <Modal
                title={copy.title}
                isOpen={isModalOpen}
                onClose={handleModalClose}
                showCloseIcon
                showLoading={isSendingFeedback}
                closeIconTooltip={copy.close}
                footerProps={{
                    secondaryButton: {
                        text: copy.cancel,
                        onClick: handleModalClose,
                    },
                    primaryButton: {
                        text: copy.submit,
                        onClick: handleSendFeedback,
                        disabled: isSendingFeedback || !hasSelectedCategory,
                    },
                }}
                width="520px"
            >
                <Stack direction="column" gap={tokens.SizingSpacing200} sx={{ width: '100%' }}>
                    <Stack direction="column" gap={tokens.SizingSpacing075}>
                        <Typography variant="headline5" color={tokens.ColorTextDefault}>
                            {copy.categoryLabel}
                        </Typography>
                        <RadioGroup
                            aria-labelledby="feedback-category-label"
                            name="feedback-category"
                            style={{ padding: '0 4px', color: tokens.ColorTextBold }}
                            value={category}
                            onChange={(event) => setCategory(event.target.value)}
                        >
                            {options}
                        </RadioGroup>
                    </Stack>
                    <Stack direction="column" gap={tokens.SizingSpacing100}>
                        <Stack direction="row" gap={tokens.SizingSpacing050} alignItems="center">
                            <Typography variant="headline5" color={tokens.ColorTextDefault}>
                                {copy.descriptionLabel}
                            </Typography>
                            <InfoTooltip style={{ flexShrink: 0 }} text={copy.descriptionTooltip} />
                        </Stack>
                        <TextField
                            label={copy.descriptionFieldLabel}
                            inputProps={{ 'aria-label': copy.descriptionAriaLabel, style: { resize: 'vertical' } }}
                            multiline
                            rows={3}
                            value={feedback}
                            onChange={(e) => {
                                setFeedback(e.target.value)
                            }}
                        />
                    </Stack>
                    {showIncidentOption ? (
                        <Stack direction="row" gap={tokens.SizingSpacing050} alignItems="center">
                            <Checkbox label={copy.incidentLabel} checked={isIncident} onChange={() => setIsIncident((prev) => !prev)} />
                            <InfoTooltip size={tokens.SizingIconS} text={copy.incidentTooltip} />
                        </Stack>
                    ) : null}
                </Stack>
                <Snackbar
                    open={isFeedbackError}
                    onClose={handleCloseErrorSnackbar}
                    autoHideDuration={10000}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <div>
                        <Alert severity="error" size="small">
                            {copy.errorMessage}
                        </Alert>
                    </div>
                </Snackbar>
            </Modal>
            <Snackbar
                open={snackbarOpen}
                onClose={handleCloseSnackbar}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <div>
                    <Alert severity="success" size="small">
                        {copy.successMessage}
                    </Alert>
                </div>
            </Snackbar>
        </>
    )
}
